import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { Currency } from '@mobi/utils/money'
import type { BetSlipErrorResponse, Campaign, InvestmentType } from '@mobi/betslip/types'
import { determineValueFromKeypad } from './helpers/determineValueFromKeypad'
import { refreshAllBets, confirmAllBets, proposeAllBets } from './asyncActions'
import { MAX_INVESTMENT_VALUE } from '@mobi/betslip/helpers/constants'
import { handleSettingServerCallRejectionState } from './helpers/stateSetters'

export const initialState: BetSlipWorkflowState = {
  activeInvestment: null,
  keypadActiveValue: null,
  isBusy: false,
  currentStatus: 'ready',
  apiErrorMessage: null,
  hostAccountBalance: null,
  isBettingDisabled: false,
  isHostUserLoggedIn: false,
  canShowInducements: false,
  visibleBettingSection: 'Singles',
  desiredBettingSection: null,
  isBettingSectionExpanded: { Singles: true, Multi: true },
  userPromoData: {
    campaigns: [],
    bonusCashCampaigns: [],
    bonusBetBalance: 0,
    bonusCashBalance: 0,
  },
}

const workflowSlice = createSlice({
  name: 'betslip/workflow',
  initialState,
  reducers: {
    setActiveInvestment(state, { payload }: PayloadAction<ActiveInvestment>) {
      state.keypadActiveValue = null
      state.activeInvestment = payload
    },

    setDesiredBettingSection(state, { payload }: PayloadAction<'Singles' | 'Multi' | null>) {
      state.desiredBettingSection = payload
    },

    setKeypadActiveValue(
      state,
      { payload: { keypadInput, isInitValue } }: PayloadAction<SetKeypadActiveValueArgs>
    ) {
      if (isInitValue) {
        const stringValue = new Currency(keypadInput.value).toString()
        const [dollars, cents] = stringValue.split('.')
        state.keypadActiveValue = new Currency(cents).value > 0 ? stringValue : dollars
        return
      }
      const currentValue = state.keypadActiveValue
      const newValue = determineValueFromKeypad({ keypadInput, currentValue })
      if (new Currency(newValue).value > MAX_INVESTMENT_VALUE) return
      state.keypadActiveValue = newValue
    },

    setWorkflowStatusToReady(state) {
      state.currentStatus = 'ready'
      state.apiErrorMessage = null
      state.keypadActiveValue = null
      state.activeInvestment = null
    },

    setBettingDisabledStatus(state, { payload }: PayloadAction<boolean>) {
      if (payload) state.currentStatus = 'ready'
      state.isBettingDisabled = payload
    },

    setBetSlipUserAccountBalanceSync: (state, { payload }: PayloadAction<number | null>) => {
      state.hostAccountBalance = payload
    },

    setBetSlipIsUserLoggedInSync(state, { payload }: PayloadAction<boolean>) {
      state.isHostUserLoggedIn = payload
    },

    setBetSlipCanShowInducementsSync(state, { payload }: PayloadAction<boolean>) {
      state.canShowInducements = payload
    },

    setVisibleBettingSection(
      state,
      { payload }: PayloadAction<BetSlipWorkflowState['visibleBettingSection']>
    ) {
      state.visibleBettingSection = payload
    },

    setBettingSectionExpandedState(
      state,
      { payload }: PayloadAction<Partial<BetSlipWorkflowState['isBettingSectionExpanded']>>
    ) {
      if (payload.Multi === false) payload.Singles = true
      if (payload.Singles === false) payload.Multi = true
      state.isBettingSectionExpanded = { ...state.isBettingSectionExpanded, ...payload }
    },

    setUserPromoData: (
      state,
      {
        payload,
      }: PayloadAction<{
        bonusBetBalance: number
        bonusCashBalance: number
        campaigns: Campaign[]
        bonusCashCampaigns: Campaign[]
      }>
    ) => {
      state.userPromoData.campaigns = payload.campaigns
      state.userPromoData.bonusBetBalance = payload.bonusBetBalance
      state.userPromoData.bonusCashBalance = payload.bonusCashBalance
      state.userPromoData.bonusCashCampaigns = payload.bonusCashCampaigns
    },
  },

  extraReducers: builder => {
    // ============
    // Propose Bets
    // ============
    builder.addCase(proposeAllBets.pending, state => {
      state.isBusy = true
      state.apiErrorMessage = null
      state.activeInvestment = null
    })

    builder.addCase(proposeAllBets.fulfilled, state => {
      state.isBusy = false
      state.currentStatus = 'proposed'
    })

    builder.addCase(proposeAllBets.rejected, (state, action) => {
      handleSettingServerCallRejectionState(state, action.payload as BetSlipErrorResponse)
    })

    // ============
    // Confirm Bets
    // ============
    builder.addCase(confirmAllBets.pending, state => {
      state.isBusy = true
      state.apiErrorMessage = null
    })

    builder.addCase(confirmAllBets.fulfilled, state => {
      state.isBusy = false
      state.currentStatus = 'bets-placed'
    })

    builder.addCase(confirmAllBets.rejected, (state, action) => {
      handleSettingServerCallRejectionState(state, action.payload as BetSlipErrorResponse)
    })

    // ============
    // Refresh Bets
    // ============
    builder.addCase(refreshAllBets.pending, state => {
      state.isBusy = true
      state.apiErrorMessage = null
    })

    builder.addCase(refreshAllBets.fulfilled, state => {
      state.isBusy = false
    })

    builder.addCase(refreshAllBets.rejected, (state, action) => {
      handleSettingServerCallRejectionState(state, action.payload as BetSlipErrorResponse)
    })
  },
})

export const {
  setActiveInvestment,
  setKeypadActiveValue,
  setWorkflowStatusToReady,
  setBettingDisabledStatus,
  setBetSlipUserAccountBalanceSync,
  setBetSlipIsUserLoggedInSync,
  setBetSlipCanShowInducementsSync,
  setVisibleBettingSection,
  setDesiredBettingSection,
  setBettingSectionExpandedState,
  setUserPromoData,
} = workflowSlice.actions

export default workflowSlice.reducer

// =====
// Types
// =====

export type BetSlipWorkflowState = {
  activeInvestment: ActiveInvestment
  keypadActiveValue: string | null
  isBusy: boolean
  currentStatus: WorkflowStep
  apiErrorMessage: string | null
  hostAccountBalance: number | null
  isBettingDisabled: boolean
  isHostUserLoggedIn: boolean
  visibleBettingSection: BettingSection
  desiredBettingSection: BettingSection | null
  isBettingSectionExpanded: Record<BettingSection, boolean>
  canShowInducements: boolean
  userPromoData: CampaignData
}

export type BettingSection = 'Singles' | 'Multi'

export type KeypadInput =
  | { type: 'denomation'; value: number }
  | { type: 'numeric'; value: string }
  | { type: 'action'; value: 'clear' | 'del' | '.' }

type WorkflowStep = 'ready' | 'proposed' | 'bets-placed'
type ActiveInvestment = { itemId: string; investmentType: InvestmentType } | null
type SetKeypadActiveValueArgs = { keypadInput: KeypadInput; isInitValue?: boolean }
type CampaignData = {
  campaigns: Campaign[]
  bonusBetBalance: number
  bonusCashBalance: number
  bonusCashCampaigns: Campaign[]
}
